import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Button, TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  deletePoster,
  getGamesData,
  getGame,
  saveGameSettings,
  getLinks,
  loadPoster,
  getProfile,
} from "../redux/asyncThunk/userActions";
import InputFileUpload from "./UploadFile";
import { formatDateTimeRu } from "../utils/formatDate";
import { sanitizePlainText } from "../utils/inputValidation";
import {
  EXAMPLE_START_POST,
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../utils/projectAccess";
import { deriveLegacyMessenger } from "../utils/messengers";
import { getContentTypeLabel, isTrainerGame, resolveGameContentType } from "../utils/contentType";
import ProjectMessengerAvailability, {
  readAvailableMessengersFromSettings,
} from "./ProjectMessengerAvailability";
import { userSlice } from "../redux/user/userSlice";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import "./index.css";

const { addMessage } = userSlice;

const prodURL = "https://club-lab.ru";
const url =
  window.location.hostname === "localhost" ? "http://localhost:3004" : prodURL;

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

const DESCRIPTION_PLACEHOLDER =
  "Этот сценарий поможет освоить вам ремонт кофемашины Макита. Вы изучите как починить и не сломать.";

export default function Settings() {
  const dispatch = useDispatch();
  const [data, setData] = useState();
  const [settings, setSettings] = useState({
    title: "",
    category: "",
    program: "",
    description: "",
    private_access: false,
    allow_replay: true,
    completion_text: "",
    author_telegram: "",
    availableMessengers: ["telegram", "max"],
    projectStatus: "draft",
  });

  const game = useSelector((state) => state.user.game);
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  useEffect(() => {
    if (!game) return;

    setSettings({
      title: game.title ?? "",
      category: game.category ?? "",
      program: game.program ?? "",
      description: game.description ?? "",
      private_access: false,
      allow_replay: game.settings?.allow_replay !== false,
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
      projectStatus: getProjectStatusFromSettings(game.settings),
    });
    setData({
      ...game,
      scenesValue: game?.nodes?.filter((el) => el.type === "group")?.length,
    });
  }, [game]);

  const handleChange = (event, property) => {
    setSettings((prev) => ({
      ...prev,
      [property]:
        typeof event.target.value === "string"
          ? sanitizePlainText(
              event.target.value,
              property === "description" || property === "completion_text"
                ? 2000
                : 200
            )
          : event.target.value,
    }));
  };

  const handleToggle = (property) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [property]: event.target.checked,
    }));
  };

  const buildSettingsPayload = () => {
    const statusFlags = settingsFromProjectStatus(
      settings.projectStatus,
      game?.settings
    );

    return mergeGameSettings(game?.settings, {
      private_access: false,
      published: statusFlags.published,
      hidden: statusFlags.hidden,
      completion_text: settings.completion_text,
      author_telegram: settings.author_telegram,
      allow_replay: settings.allow_replay,
      availableMessengers: settings.availableMessengers,
      messenger: deriveLegacyMessenger(settings.availableMessengers),
      contentType: resolveGameContentType(game),
    });
  };

  const handleUpdateSettings = () => {
    dispatch(
      saveGameSettings({
        game: game._id,
        title: settings.title,
        description: settings.description,
        category: settings.category,
        program: settings.program,
        settings: buildSettingsPayload(),
        onSucces: () => {
          dispatch(getLinks({ userId: user.id }));
          dispatch(getGame({ gameId: game._id }));
        },
      })
    );
  };

  const handleAddPoster = (event, id) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      const base64String = reader.result.split(",")[1];
      dispatch(
        loadPoster({
          gameId: id,
          base64String,
          onSucces: () => dispatch(getGame({ gameId: game._id })),
        })
      );
    };
  };

  const handleShowExamplePost = () => {
    dispatch(
      addMessage({
        type: "start-post",
        preview: EXAMPLE_START_POST,
      })
    );
  };

  const posterTitle = settings.title || "Название вашего сценария";
  const posterDescription =
    settings.description || DESCRIPTION_PLACEHOLDER;
  const isTrainer = isTrainerGame(game);

  return (
    <div className="app-editor-settings">
      <div className="app-editor-settings__top">
        <section className="app-settings-card app-settings-card--meta">
          <h3 className="app-settings-card__title">Основное</h3>

          <div className="app-settings-meta-grid">
            <div className="app-settings-field app-settings-field--id">
              <span className="app-settings-field__label">ID сценария</span>
              <span className="app-settings-id">{game?._id}</span>
            </div>

            <div className="app-settings-field">
              <span className="app-settings-field__label">Тип проекта</span>
              <span className="app-settings-type-badge">
                {getContentTypeLabel(resolveGameContentType(game))}
              </span>
            </div>

            <div className="app-settings-field">
              <span className="app-settings-field__label">
                Последнее изменение
              </span>
              <span className="app-settings-meta">
                {formatDateTimeRu(game?.updatedAt || game?.createdAt)}
              </span>
            </div>
          </div>

          <div className="app-settings-field app-settings-field--status">
            <span className="app-settings-field__label">Статус проекта</span>
            <RadioGroup
              row
              className="app-settings-status"
              value={settings.projectStatus}
              onChange={(event) => handleChange(event, "projectStatus")}
            >
              <FormControlLabel
                value="published"
                control={<Radio size="small" />}
                label="Опубликован"
                className="app-settings-status__option"
              />
              <FormControlLabel
                value="draft"
                control={<Radio size="small" />}
                label="В работе"
                className="app-settings-status__option"
              />
              <FormControlLabel
                value="hidden"
                control={<Radio size="small" />}
                label="Остановлен"
                className="app-settings-status__option"
              />
            </RadioGroup>
          </div>

          <div className="app-settings-save">
            <Button
              variant="contained"
              size="small"
              className="app-btn app-btn--primary app-btn--compact"
              onClick={handleUpdateSettings}
            >
              Сохранить
            </Button>
          </div>
        </section>

        <section className="app-settings-card app-settings-card--client">
          <h3 className="app-settings-card__title">Поведение для клиента</h3>

          <div className="app-settings-client-grid">
            <div className="app-settings-toggle">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.allow_replay}
                    onChange={handleToggle("allow_replay")}
                  />
                }
                label="Разрешить повторное прохождение"
              />
              <p className="app-settings-toggle__hint">
                Если выключено, клиент не сможет пройти сценарий заново.
              </p>
            </div>

            <div className="app-settings-field app-settings-field--wide">
              <label
                className="app-settings-field__label"
                htmlFor="completion_text"
              >
                Текст при завершении
              </label>
              <TextField
                id="completion_text"
                placeholder="Сообщение бота в конце сценария"
                variant="outlined"
                multiline
                minRows={2}
                fullWidth
                value={settings.completion_text}
                onChange={(event) => handleChange(event, "completion_text")}
                sx={fieldSx}
              />
            </div>

            <div className="app-settings-field app-settings-field--wide">
              <label
                className="app-settings-field__label"
                htmlFor="author_telegram"
              >
                Связаться с HR / поддержкой
              </label>
              <TextField
                id="author_telegram"
                placeholder="@username или https://t.me/username"
                variant="outlined"
                size="small"
                fullWidth
                value={settings.author_telegram}
                onChange={(event) => handleChange(event, "author_telegram")}
                sx={fieldSx}
              />
              <p className="app-settings-field__hint">
                Telegram-ссылка для кнопки связи с ответственным в боте.
              </p>
            </div>
          </div>
        </section>

        <section className="app-settings-card app-settings-card--poster">
          <h3 className="app-settings-card__title">
            {isTrainer
              ? "Стартовый пост вашего тренажёра"
              : "Стартовый пост вашего сценария"}
          </h3>

          <div className="app-settings-poster-grid">
            <div className="app-settings-poster-fields">
              <div className="app-settings-field">
                <label className="app-settings-field__label" htmlFor="title">
                  Название
                </label>
                <TextField
                  onChange={(event) => handleChange(event, "title")}
                  id="title"
                  placeholder="Название вашего сценария"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={settings?.title ?? ""}
                  sx={fieldSx}
                />
              </div>

              <div className="app-settings-field">
                <label className="app-settings-field__label" htmlFor="program">
                  Программа / отдел
                </label>
                <TextField
                  onChange={(event) => handleChange(event, "program")}
                  id="program"
                  placeholder="Например: Онбординг, Отдел продаж"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={settings?.program ?? ""}
                  sx={fieldSx}
                />
              </div>

              <div className="app-settings-field">
                <label
                  className="app-settings-field__label"
                  htmlFor="description"
                >
                  Текст стартового поста
                </label>
                <TextField
                  id="description"
                  placeholder={DESCRIPTION_PLACEHOLDER}
                  variant="outlined"
                  multiline
                  minRows={3}
                  fullWidth
                  value={settings?.description ?? ""}
                  onChange={(event) => handleChange(event, "description")}
                  sx={fieldSx}
                />
              </div>

              <div className="app-settings-poster__actions">
                <InputFileUpload
                  handleAddPoster={(event) => handleAddPoster(event, game._id)}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className="app-btn app-btn--secondary app-btn--compact"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={handleShowExamplePost}
                >
                  Пример стартового поста
                </Button>
              </div>
            </div>

            <div className="app-settings-poster">
              {data?.poster ? (
                <Tooltip title="Удалить постер" placement="top">
                  <DeleteIcon
                    className="app-settings-poster__delete"
                    onClick={() => {
                      dispatch(
                        deletePoster({
                          gameId: game._id,
                          onSucces: () =>
                            dispatch(getGamesData({ id: user.id })),
                        })
                      );
                    }}
                  />
                </Tooltip>
              ) : null}

              <div className="app-settings-poster__media">
                {data?.poster ? (
                  <img src={`${url}/${data?.poster}`} alt="Постер сценария" />
                ) : (
                  <div className="app-settings-poster__empty">
                    <ImageNotSupportedOutlinedIcon />
                    <span>Загрузите изображение</span>
                  </div>
                )}
              </div>

              <div className="app-settings-poster__caption">
                <p className="app-settings-poster__caption-title">
                  Название: {posterTitle}
                </p>
                <p>{posterDescription}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="app-editor-settings__access-wrap">
      <section className="app-settings-card app-settings-card--access app-settings-card--access-compact">
        <h3 className="app-settings-card__title app-settings-card__title--left">
          Доступ для команды
        </h3>

        <div className="app-settings-access-body">
          <ProjectMessengerAvailability
            gameId={game?._id}
            value={settings.availableMessengers}
            onChange={(next) =>
              setSettings((prev) => ({
                ...prev,
                availableMessengers: next,
              }))
            }
            compact
            hintExtra={
              <>
                {" "}
                <Link to="/main/price">Тарифы</Link>
              </>
            }
          />
        </div>
      </section>
      </div>
    </div>
  );
}
